/**
 * Sharpet art contract. Every image a species ships must follow it, so the
 * art can be swapped (placeholder -> final drawings -> sprite sheets) by
 * replacing files, never by changing the components that draw them.
 *
 *  - Files: art/<species>/<mood>.webp (or .png / .svg), one per entry in
 *    SHARPET_MOODS. Nothing here lists them: the bundler finds whatever is
 *    in the folder. The artist's sheets also carry angry and surprised
 *    poses; they are stored alongside, unused until a mood needs them.
 *  - Canvas: 512x512, transparent background.
 *  - Framing: the character is centred horizontally and stands on the
 *    baseline (feet / bottom at y=481), at the same size in every mood.
 *  - Moods: same pose and position in each; only the expression changes.
 *    A missing mood falls back to idle, so art can arrive in batches, and
 *    a species without an idle image is not offered to members at all.
 *  - Anchors: canvas coordinates where layered cosmetics attach. `head` is
 *    the crown (a hat's bottom-centre sits here), `neck` is the collar
 *    line. Nothing draws on them yet; they are part of the brief so the
 *    final art leaves room for them.
 *
 * Images go through the bundler rather than public/ so each gets a content
 * hash: replacing a file busts browser caches on deploy.
 */
export const SHARPET_CANVAS = Object.freeze({
  width: 512,
  height: 512,
  baselineY: 481,
})

// The mood vocabulary shared with the API: the backend decides which mood
// a pet is in, the art only has to draw each one.
export const SHARPET_MOODS = Object.freeze([
  'idle',
  'happy',
  'hungry',
  'sleepy',
  'sad',
])

export const DEFAULT_SPECIES = 'sharpei'

const DEFAULT_ANCHORS = Object.freeze({
  head: {x: 256, y: 92},
  neck: {x: 256, y: 292},
})

// The species the API accepts (lib/endpoints/sharpets/sharpet-species.js
// on the backend holds the same keys), in the order the picker shows them:
// the Shar-Pei first, it is the house pet.
const SPECIES_LABELS = [
  ['sharpei', 'Shar-Pei'],
  ['axolotl', 'Axolotl'],
  ['cat', 'Cat'],
  ['fox', 'Fox'],
  ['hamster', 'Hamster'],
  ['hedgehog', 'Hedgehog'],
  ['owl', 'Owl'],
  ['penguin', 'Penguin'],
  ['platypus', 'Platypus'],
  ['raccoon', 'Raccoon'],
  ['sloth', 'Sloth'],
  ['turtle', 'Turtle'],
]

const art = require.context(
  './art',
  true,
  /^\.\/[a-z]+\/[a-z]+\.(png|webp|svg)$/
)

const moodsBySpecies = art.keys().reduce((all, path) => {
  const [, species, mood] = path.match(/^\.\/([a-z]+)\/([a-z]+)\./)
  // SVGs come through svgr as a module; rasters as the URL itself.
  const asset = art(path)
  all[species] = {...all[species], [mood]: asset.default || asset}
  return all
}, {})

export const SHARPET_SPECIES = Object.freeze(
  Object.fromEntries(
    SPECIES_LABELS.map(([key, label]) => [
      key,
      {
        label,
        anchors: DEFAULT_ANCHORS,
        moods: moodsBySpecies[key] || {},
      },
    ])
  )
)

// What a member may pick: only species whose art has landed.
export const AVAILABLE_SPECIES = Object.freeze(
  Object.keys(SHARPET_SPECIES).filter((key) => SHARPET_SPECIES[key].moods.idle)
)

/**
 * The species definition and the image for a mood, falling back to the
 * default species and to idle so an unknown value never renders nothing.
 */
export const resolveSharpetArt = (species, mood) => {
  const definition = SHARPET_SPECIES[species]?.moods.idle
    ? SHARPET_SPECIES[species]
    : SHARPET_SPECIES[DEFAULT_SPECIES]
  return {
    definition,
    src: definition.moods[mood] || definition.moods.idle,
  }
}
